
import {Vue, Component, Prop, Watch} from "vue-property-decorator";
import {formRules} from "@/utils/formRules";
import {Getter} from "vuex-class";
import { CONNECTED_BOX_PROVIDERS } from "@/utils/connectedBoxes";
import QrCodeScannerWrapper from "@/components/qrcode/QrCodeScannerWrapper.vue";
import SvgIcon from "@jamescoyle/vue-icon";
import { mdiAccessPointNetwork, mdiPencilOutline, mdiPlus } from "@mdi/js";

@Component({
  components: { QrCodeScannerWrapper, SvgIcon},
})
export default class RelayCreateOrEditBox extends Vue {
    @Prop({default: null})
    public boxes: any[];

    @Prop({default: null})
    public box: any;

    @Prop()
    public buildingId: number;

    @Prop({default: () => ({})})
    public availableOptions: any;

    @Prop({default: false})
    public hasEditRight: boolean;

    @Prop({default: false})
    private readonly onlyConnectedBox: boolean;

    @Getter private isAdmin: boolean;

    private dialog: boolean = false;
    private rules: object = formRules;
    private valid: boolean = false;
    private loading: boolean = false;
    private boxData: any = {
      provider: null,
      externalId: null,
      activationCode: null,
      codeAsQrCodeOption: false,
      newTags: [],
    };
    private searchTags: string = null;
    private tags: any = [];
    private isBoxConnected: boolean = false;
    private connectedBoxesProviders = CONNECTED_BOX_PROVIDERS;
    private loadingQrCodeStreamer: boolean = false;
    private hasScannedOnboardingQrCode: boolean = false;
    private mdiPencilOutline = mdiPencilOutline;
    private mdiPlus = mdiPlus;
    private mdiAccessPointNetwork = mdiAccessPointNetwork;
    private loadingConnectionTest: boolean = false;

    get relayId(): string {
        return this.$route.params?.id;
    }
    get lang(): string {
        return this.$root.$i18n.locale;
    }
    get canUpdateBox(): boolean {
        return this.hasEditRight
          && this.box
          && this.valid
          && this.changed(this.box, this.boxData)
          && !this.boxReduced(this.box, this.boxData);
    }
    get displayQrCodeStreamer(): boolean {
      // Only display QR Code streamer when creating a new Snapaccess box
        return !this.box
          && this.boxData.provider === this.connectedBoxesProviders.SNAPACCESS
          && !this.hasScannedOnboardingQrCode;
    }
    get canCreateConnectedBox(): boolean {
        return (this.isAdmin && this.availableOptions.connectedBoxes)
          || ((this.isAdmin || this.hasEditRight) && this.availableOptions.masterlocks);
    }
    get possibleConnectedBoxesProviders(): Array<{ value: string, text: string }> {
      const possibleProviders: any[] = [];
      const formattedProvider = (provider: string) => ({
        value: provider,
        text: this.$i18n.t(`connectedBox.providers.classic.${provider}`),
      });
      // For now, only the admin can create Acses boxes
      if (this.isAdmin && this.availableOptions.connectedBoxes) {
        possibleProviders.push(formattedProvider(CONNECTED_BOX_PROVIDERS.ACSES));
      }
      if (this.availableOptions.masterlocks) {
        possibleProviders.push(formattedProvider(CONNECTED_BOX_PROVIDERS.SNAPACCESS));
      }
      return possibleProviders;
    }
    get isMasterlock(): boolean {
      return this.box?.ConnectedBox?.provider === CONNECTED_BOX_PROVIDERS.SNAPACCESS;
    }
    get isAcses(): boolean {
      return this.box?.ConnectedBox?.provider === CONNECTED_BOX_PROVIDERS.ACSES;
    }
    get currentBridgeConnection(): any|undefined {
      return this.box?.ConnectedBox?.BridgeConnections?.find((bc) => !bc.endConnectionDate);
    }
    get lastBoxActivation(): any|undefined {
      return this.box?.BoxActivations?.[0];
    }
    get boxActivationState(): any|null {
      const lastActivation = this.lastBoxActivation;
      if (!lastActivation) { return null; }
      return {
        name: lastActivation.endDate ? "inactive" : "active",
        date: lastActivation.endDate || lastActivation.startDate,
      };
    }

    @Watch("dialog", {deep: true})
    public async handlerDialog() {
        this.resetBoxDataValues();
        this.isBoxConnected = this.onlyConnectedBox;
        if (this.box) {
            if (this.box.ConnectedBox) {
              this.isBoxConnected = true;
              this.box.provider = this.box.ConnectedBox.provider;
              this.box.externalId = this.box.ConnectedBox.externalId;
              this.box.activationCode = this.box.ConnectedBox.activationCode;
              this.box.active = !this.lastBoxActivation?.endDate;
              this.box.codeAsQrCodeOption = this.box.ConnectedBox.codeAsQrCodeOption;
            }
            this.box.newTags = await this.$store.dispatch("boxes/getTags", { boxId: this.box.id });
            this.boxData = JSON.parse(JSON.stringify(this.box));
        }
    }

    @Watch("isBoxConnected")
    public handlerIsConnected(newVal: boolean, oldVal: boolean): void {
        // Reset fields when this is a box creation
      if (!this.box) {
            this.resetBoxDataValues();
            const mylobyToConnected: boolean = !oldVal && newVal;
            if (mylobyToConnected) {
              if (this.possibleConnectedBoxesProviders.length === 1) {
                this.boxData.provider = this.possibleConnectedBoxesProviders[0].value;
              }
            }
        }
    }

    private mounted() {
         // this.getTags();
         // moved to @focus="getTags"
    }

    private changed = (oldBoxData: any, newBoxData: any) =>
        newBoxData.sheets !== oldBoxData.sheets ||
        newBoxData.columns !== oldBoxData.columns ||
        newBoxData.lines !== oldBoxData.lines ||
        newBoxData.name !== oldBoxData.name ||
        newBoxData.number !== oldBoxData.number ||
        newBoxData.active !== oldBoxData.active ||
        newBoxData.codeAsQrCodeOption !== oldBoxData.codeAsQrCodeOption ||
        JSON.stringify(newBoxData.newTags) !== JSON.stringify(oldBoxData.newTags)

    private boxReduced = (oldDimensions: any, newDimensions: any) =>
        newDimensions.sheets < oldDimensions.sheets ||
        newDimensions.columns < oldDimensions.columns ||
        newDimensions.lines < oldDimensions.lines

    private getSize = (boxData: any): number => boxData.sheets * boxData.columns * boxData.lines;

    private largerSheets(v) {
        return v >= this.box?.sheets || this.$i18n.t("box.reduced");
    }

    private largerColumns(v) {
        return v >= this.box?.columns || this.$i18n.t("box.reduced");
    }

    private largerLines(v) {
        return v >= this.box?.lines || this.$i18n.t("box.reduced");
    }

    private resetBoxDataValues() {
      this.boxData = {
        name: null,
        number: null,
        sheets: null,
        columns: null,
        lines: null,
        tags: [],
        newTags: [],
        provider: null,
        externalId: null,
        size: null,
        active: null,
      };
    }

    private onDecode(decodedString: string) {
        if (decodedString) {
          this.loadingQrCodeStreamer = true;
          this.$nextTick().then(() => {
            const [externalId, activationCode] = decodedString.split(":");
            if (!externalId || !activationCode) {
              // If stream didn't work, display error message and re-activate it
              this.$store.commit("alerts/displayError", {
                msg: this.$i18n?.t("connectedBoxOnboarding.unknownQrCode"),
              });
            } else {
              // Else, automatically fill connected box data
              this.boxData.externalId = externalId;
              this.boxData.activationCode = activationCode;
              this.hasScannedOnboardingQrCode = true;
            }
            this.loadingQrCodeStreamer = false;
          });
        }
    }

    private async createButtonClick(): Promise<void> {
      if (this.boxData.provider === CONNECTED_BOX_PROVIDERS.ACSES && this.boxData.externalId && !this.boxData.size) {
        // For connected box, if the id is provided but not the size,
        // we need to fetch it first for validation
        await this.fetchConnectedBoxSize(this.boxData).then((res: number) => {
          if (res) {
            this.boxData.size = res;
          } else {
            this.$store.commit("alerts/displayError", {
              msg: this.$i18n?.t("connectedBox.sizeUnknown"),
            });
          }
        });
      } else {
        await this.createBox(this.boxData);
      }
    }

    private async createBox(data: any) {
      this.loading = true;
      this.$store.dispatch("buildings/addBox", {
          buildingId: this.buildingId,
            box: {
                name: data.name,
                number: data.number,
                sheets: data.sheets,
                columns: data.columns,
                lines: data.lines,
                tags: data.newTags,
                // Connected box data
                externalId: data.externalId,
                activationCode: data.activationCode,
                provider: data.provider,
                size: data.size,
            },
        })
          .then(() => this.successCallback(data.provider === CONNECTED_BOX_PROVIDERS.SNAPACCESS
            ? "connectedBox.initializationLatency"
            : "actions.created",
          ))
          .catch((err) => this.failureCallback(err))
          .finally(() => { this.loading = false; });
    }

    private fetchConnectedBoxSize(data) {
      this.loading = true;
      return this.$store.dispatch("connectorConnectedBoxes/getSize", {
        externalId: data.externalId,
        provider: data.provider,
      })
        .catch((err) => this.failureCallback(err))
        .finally(() => { this.loading = false; });
    }

    private updateBox(data: any) {
        this.loading = true;
        this.$store.dispatch("boxes/update", {
            id: this.box.id,
            box: {
                id: this.box.id,
                // number: data.number,
                sheets: data.sheets,
                columns: data.columns,
                lines: data.lines,
                name: data.name,
                tags: data.newTags,
                active: data.active,
                codeAsQrCodeOption: data.codeAsQrCodeOption !== this.box.codeAsQrCodeOption
                  ? data.codeAsQrCodeOption
                  : undefined,
                // For now, there is no connected box update possible
                // externalId: data.externalId,
                // provider: data.provider,
            },
        })
          .then(() => this.successCallback("actions.saved"))
          .catch((err) => this.failureCallback(err))
          .finally(() => { this.loading = false; });
    }

    private async testConnection(): Promise<void> {
      this.loadingConnectionTest = true;
      await this.$store.dispatch("connectorConnectedBoxes/testConnection", {
        externalId: this.box.externalId,
        provider: this.box.provider,
      }).then(() => {
        this.$store.commit("alerts/displaySuccess", {
          msg: this.$t("key.actions.connectedBox.testConnection.success"),
        });
      }).catch((error) => {
        this.$store.commit("alerts/displayHttpError", { error });
      }).finally(() => {
        this.loadingConnectionTest = false;
      });
    }

    private deleteBox() {
        this.loading = true;
        this.$store.dispatch("boxes/remove", {
            id: this.box.id,
        })
          .then(() => this.successCallback("actions.deleted"))
          .catch((err) => this.failureCallback(err))
          .finally(() => { this.loading = false; });
    }

    private successCallback(message: string) {
      this.$store.commit("alerts/displaySuccess", {
        msg: this.$i18n?.t(message),
      });
      this.dialog = false;
      this.$emit("reset");
    }

    private failureCallback(error: any) {
      this.$store.commit("alerts/displayHttpError", { error });
    }

    private getTags(): void {
        this.$store.dispatch("tags/getAll", {
            query: {
                search: this.searchTags,
                lang: this.lang,
            },
        })
        .then((res) => {
            this.tags = res;
        });
    }
    private removeTags(item): void {
        const index: number = this.boxData.newTags.findIndex((i) => i.id === item.id);
        if (index >= 0) {
            this.boxData.newTags.splice(index, 1);
        }
    }
    private removeTagDuplicates(completeList, duplicates): any[] | null {
        if (!completeList || !duplicates) {
            return null;
        }
        return completeList.filter((item: any) =>
            !duplicates.map((elem: any) => elem?.id)?.includes(item?.id));
    }
}
